// ---------------------------------------------------------------------------
// The real receipt, for a sale that actually happened.
//
// Distinct from the Phase 2 preview in one important way: everything on it
// comes from the saved sale, not from the cart. The receipt number, the
// exchange rate, the prices and the payment lines are all read back from the
// database, so a reprint next year shows exactly what was charged - even if
// the product has since been renamed or repriced.
//
// A reprint is marked as one on the paper. An unmarked duplicate receipt is
// one of the easier ways to disguise a missing payment.
//
// The paper itself is drawn by ReceiptSheet, which is the SAME component the
// Receipt Designer previews. This file decides which layout to hand it:
//
//   * a sale carrying `receiptDesignVersionId` prints with THAT version, so a
//     reprint reproduces the paper the customer was handed;
//   * a sale with none - rung up before the designer existed - prints with
//     whatever is active now, which is the layout it would have had anyway.
//
// PRINT MEANS THE SHOP'S PRINTER. Pressing Print rasterises the sheet below
// and posts it to the print queue for the CASHIER_RECEIPT role, exactly as a
// completed sale does. It does NOT open the browser's print dialogue: a
// cashier reprinting a receipt for a customer standing at the counter should
// not be choosing a destination, a paper size and a colour mode, and the
// roll they need is not in that list anyway - it is on a USB cable the
// server talks to.
// ---------------------------------------------------------------------------

import { useRef } from 'react';
import { Printer } from 'lucide-react';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { usePrintConfig, useReceiptConfigForVersion } from '../../api/designs.api';
import { usePrinterDevices } from '../../api/printerDevices.api';
import { useNetworkReprint } from '../../hooks/useNetworkReprint';
import { toast } from '../../stores/toastStore';
import { ReceiptSheet } from '../print/ReceiptSheet';
import { FALLBACK_RECEIPT_CONFIG } from '../../types/design';
import type { ReceiptWidth } from '../../utils/printPaper';
import type { Sale } from '../../types';

interface SaleReceiptProps {
  open: boolean;
  onClose: () => void;
  sale: Sale;
  /// Set when this receipt belongs to a DELIVERY ORDER whose account is
  /// still out - the read-only preview from «حسابات التوصيل». The `sale`
  /// prop is then the order's frozen snapshot in the Sale shape, and a
  /// manual print is recorded against the ORDER, not a sale that does not
  /// exist yet. Everything else - renderer, design freezing, print pipeline,
  /// printer routing - is identical, which is the point.
  orderId?: string | null;
  /// Marks the paper as a duplicate.
  shopNameEn: string;
  shopNameAr: string;
  shopAddress: string;
  shopPhone: string;
  receiptFooter: string;
}

/// Is there a cashier printer to send to at all?
///
/// BOTH TRANSPORTS COUNT. This used to ask only for an IP address, which is
/// how the shop ended up with no direct-print button at all: their receipt
/// printer is on a USB cable, so it has a Windows queue name and no address,
/// and the check said "no printer" about a printer that prints every receipt
/// of every sale. The role owns its transport - the profile says which - and
/// the answer here has to match.
function useReceiptPrinter() {
  // THE PRINTER THE SHOP CHOSE, not whichever holds a role.
  //
  // This used to look for `role === 'CASHIER_RECEIPT'`, which answers "the
  // oldest printer that has ever been the receipt printer". Once a shop owns
  // several and moves its receipts to a different one, that is the wrong
  // machine - and a customer asking for a copy of their receipt would have
  // watched it come out at the back.
  const { data: printers } = usePrinterDevices();
  const printer = printers?.find((row) => row.isDefaultReceipt) ?? null;

  // BOTH TRANSPORTS COUNT. Asking only for an IP address is how the shop ended
  // up with no direct-print button at all: their receipt printer is on a USB
  // cable, so it has a queue name and no address, and the check said "no
  // printer" about the one that prints every receipt of every sale.
  const ready = Boolean(
    printer?.enabled &&
      (printer.connection === 'USB_WINDOWS'
        ? Boolean(printer.printerName?.trim())
        : printer.connection === 'NETWORK_TCP'
          ? Boolean(printer.ipAddress)
          : false),
  );

  return { printer, ready };
}

export function SaleReceipt({
  open,
  onClose,
  sale,
  orderId = null,
  shopNameEn,
  shopNameAr,
  shopAddress,
  shopPhone,
  receiptFooter,
}: SaleReceiptProps) {
  const { t } = useI18n();

  const sheet = useRef<HTMLDivElement>(null);
  const reprint = useNetworkReprint();
  const { printer: receiptPrinter, ready: networkReady } = useReceiptPrinter();

  const { data: printConfig } = usePrintConfig();
  // The layout this particular sale was rung up under, when it has one.
  const stamped = useReceiptConfigForVersion(sale.receiptDesignVersionId);

  // The stamped layout wins, then today's, then the built-in one. The last
  // step matters: a till whose layout request failed must still put a receipt
  // in a customer's hand.
  const config = stamped.data?.receipt ?? printConfig?.receipt ?? FALLBACK_RECEIPT_CONFIG;

  // Whether the layout on screen is the REAL one or the fallback. Automatic
  // printing waits for the real one, so the system never prints one layout and
  // then shows another.
  const layoutSettled = Boolean(stamped.data?.receipt ?? printConfig?.receipt);

  // The preview on screen is the same width as the paper, so what a cashier
  // checks is what comes out.
  //
  // THE WIDTH COMES FROM THE DESIGN AND NOWHERE ELSE. It used to fall back to
  // a `pos.receipt_width` setting while the layout was loading, which meant a
  // shop could set 58 mm in one screen and arrange an 80 mm layout in another
  // - and the paper would be whichever the code read first. The design owns
  // both now, so they cannot disagree.
  const paperWidth: ReceiptWidth = (config.widthMm === 58 ? 58 : 80) as ReceiptWidth;

  /// Print this receipt on the printer the shop chose for receipts.
  ///
  /// A REPRINT job, not a retry: the original was not deduplicated away, and
  /// the copy carries the same design version the sale was rung up under, so
  /// it comes out looking like the paper the customer was handed.
  ///
  /// WAITS FOR THE LAYOUT. Rasterising before the design arrives would
  /// photograph the fallback sheet and then swap the screen to the real one -
  /// the one case where what you saw is not what came out.
  const printOnCashierPrinter = async () => {
    if (!layoutSettled) return;

    const status = await reprint.send({
      printerRole: 'CASHIER_RECEIPT',
      printerProfileId: receiptPrinter?.id ?? null,
      element: () => sheet.current?.querySelector<HTMLElement>('.receipt-sheet') ?? null,
      widthMm: paperWidth,
      // A delivery-order preview records its copy against the ORDER; a sale's
      // receipt records it against the sale. Never both, never neither.
      saleId: orderId ? null : sale.id,
      orderId,
      designVersionId: sale.receiptDesignVersionId,
    });

    // What each outcome is allowed to claim:
    //
    //   SENT    the bytes reached the printer. NOT "it printed" - a spooler
    //           and a raw socket both answer before any paper moves.
    //   QUEUED  written down, not yet away. The queue will retry it, and
    //           saying so beats a red error about something still in hand.
    //   else    it did not go. No browser dialogue behind it: the shop asked
    //           for its own printer, and a Windows print box appearing
    //           instead would be answering a different question.
    if (status === 'SENT') toast.success(t.salePrint.sentAgain);
    else if (status === 'NO_AGENT') toast.error(t.salePrint.agentSilentToast, t.salePrint.agentSilentDetail);
    else if (status === 'QUEUED' || status === 'PRINTING') toast.info(t.salePrint.onItsWay);
    else toast.error(t.salePrint.sendAgainFailed, reprint.lastError() ?? undefined);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t.orders.receipt} ${sale.receiptNumber}`}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary no-print" onClick={onClose}>
            {t.common.close}
          </button>
          {/* ONE print button, and it prints on the shop's printer. There
              used to be two - "send another copy" beside a "Print" that
              opened the browser dialogue - and the one that actually reached
              the roll was hidden whenever the printer was on USB. */}
          <button
            type="button"
            className="btn-primary no-print"
            disabled={reprint.isSending || !layoutSettled}
            onClick={() => void printOnCashierPrinter()}
          >
            <Printer className="h-4 w-4" aria-hidden="true" />
            {reprint.isSending ? t.salePrint.sendingAgain : t.pos.printPreview}
          </button>
        </>
      }
    >
      {/* Said BEFORE the button is pressed, not as an error afterwards. The
          server would answer "that printer is not set up yet" either way,
          but a cashier with a customer waiting should know first. */}
      {!networkReady ? (
        <p className="no-print mb-3 rounded-control border border-antique-gold/40 bg-antique-gold/10 p-2.5 text-xs text-antique-gold-deep">
          {t.salePrint.notConfiguredCashier}
        </p>
      ) : null}

      <div className="flex justify-center" ref={sheet}>
          <ReceiptSheet
            config={{
              ...config,
              // The footer setting predates the designer and some shops still
              // fill it in. A design with its own footer text wins; otherwise
              // the old setting is honoured rather than silently dropped.
              footerText: config.footerText || receiptFooter,
            }}
            sale={sale}
            shop={{
              nameEn: shopNameEn,
              nameAr: shopNameAr,
              address: shopAddress,
              phone: shopPhone,
            }}
          />
      </div>
    </Modal>
  );
}
